/**
 * CustomerAdsManager — admin control panel for personal customers' market ads.
 * Source of truth: db_admin.customer_ads (written by the Customer Platform).
 * One instance per category: phone_number | car | car_plate.
 * Cards with: status badge, customer info, edit / pause-activate / reject / delete
 * + "إدراج إعلان" for a specific customer by email lookup.
 */
import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Phone, Car, Hash, Loader2, RefreshCw, Pencil, Trash2, X, Save, Plus,
  Search, Pause, Play, Ban, AlertCircle, Mail, User, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { getAuthHeaders } from '../../../utils/authHeaders';
import UAEPlate, { hasPlateDesign } from '../../../components/plates/UAEPlate';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  phone_number: { icon: Phone, color: 'text-blue-500', bg: 'bg-blue-500/20', border: 'hover:border-blue-500/40', titleAr: 'أرقام الهواتف', titleEn: 'Phone Numbers' },
  car: { icon: Car, color: 'text-emerald-500', bg: 'bg-emerald-500/20', border: 'hover:border-emerald-500/40', titleAr: 'السيارات', titleEn: 'Cars' },
  car_plate: { icon: Hash, color: 'text-amber-500', bg: 'bg-amber-500/20', border: 'hover:border-amber-500/40', titleAr: 'أرقام السيارات (اللوحات)', titleEn: 'Car Plates' },
};

const STATUS_META = {
  active: { ar: 'نشط', en: 'Active', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
  paused: { ar: 'موقوف مؤقتاً', en: 'Paused', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  rejected: { ar: 'مرفوض', en: 'Rejected', cls: 'bg-red-500/15 text-red-300 border-red-500/40' },
  pending: { ar: 'قيد المراجعة', en: 'Pending Review', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/40' },
};

function adHeadline(ad, isAr) {
  if (ad.category === 'phone_number') return ad.phone_number || ad.number || ad.title || '—';
  if (ad.category === 'car') {
    const parts = [ad.make, ad.model, ad.year].filter(Boolean).join(' ');
    return parts || ad.title || '—';
  }
  if (ad.category === 'car_plate') {
    const p = ad.plate || {};
    const parts = [p.emirate, p.code, p.number].filter(Boolean).join(' · ');
    return parts || ad.title || '—';
  }
  return ad.title || '—';
}

const EMPTY_CREATE = {
  customer_email: '', title: '', description: '', price: '', currency: 'AED',
  phone_number: '', make: '', model: '', year: '',
  plate_emirate: '', plate_code: '', plate_number: '', status: 'active',
};

export default function CustomerAdsManager({ category, language = 'ar', onBack }) {
  const isAr = language === 'ar';
  const meta = CATEGORY_META[category];
  const Icon = meta.icon;

  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  // create-for-customer modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [lookupState, setLookupState] = useState(null); // null | 'loading' | {customer} | 'notfound'
  const [creating, setCreating] = useState(false);

  const fetchAds = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ category });
      if (search.trim()) params.set('search', search.trim());
      if (statusFilter) params.set('status', statusFilter);
      const res = await axios.get(`${API_URL}/api/admin/customer-ads?${params}`, { headers: getAuthHeaders() });
      setAds(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل تحميل الإعلانات' : 'Failed to load ads'));
    } finally {
      setLoading(false);
    }
  }, [category, search, statusFilter, isAr]);

  useEffect(() => { fetchAds(); }, [fetchAds]);

  const setStatus = async (ad, status) => {
    setBusyId(ad.id);
    try {
      await axios.put(`${API_URL}/api/admin/customer-ads/${ad.id}/status`, { status }, { headers: getAuthHeaders() });
      setAds((list) => list.map((a) => (a.id === ad.id ? { ...a, status } : a)));
      toast.success(isAr ? `✓ الحالة: ${STATUS_META[status].ar}` : `✓ Status: ${STATUS_META[status].en}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل تغيير الحالة' : 'Status change failed'));
    } finally {
      setBusyId(null);
    }
  };

  const deleteAd = async (ad) => {
    setBusyId(ad.id);
    try {
      await axios.delete(`${API_URL}/api/admin/customer-ads/${ad.id}`, { headers: getAuthHeaders() });
      setAds((list) => list.filter((a) => a.id !== ad.id));
      setConfirmDelete(null);
      toast.success(isAr ? '✓ تم حذف الإعلان' : '✓ Ad deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الحذف' : 'Delete failed'));
    } finally {
      setBusyId(null);
    }
  };

  const openEdit = (ad) => {
    setEditing(ad);
    setEditForm({
      title: ad.title || '',
      description: ad.description || '',
      price: ad.price ?? '',
      phone_number: ad.phone_number || '',
      make: ad.make || '',
      model: ad.model || '',
      year: ad.year ?? '',
      plate_emirate: ad.plate?.emirate || '',
      plate_code: ad.plate?.code || '',
      plate_number: ad.plate?.number || '',
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = { title: editForm.title, description: editForm.description };
      if (editForm.price !== '') payload.price = Number(editForm.price);
      if (category === 'phone_number') payload.phone_number = editForm.phone_number;
      if (category === 'car') {
        payload.make = editForm.make;
        payload.model = editForm.model;
        if (editForm.year !== '') payload.year = Number(editForm.year);
      }
      if (category === 'car_plate') {
        payload.plate_emirate = editForm.plate_emirate;
        payload.plate_code = editForm.plate_code;
        payload.plate_number = editForm.plate_number;
      }
      const res = await axios.patch(`${API_URL}/api/admin/customer-ads/${editing.id}`, payload, { headers: getAuthHeaders() });
      setAds((list) => list.map((a) => (a.id === editing.id ? res.data.ad : a)));
      setEditing(null);
      toast.success(isAr ? '✓ تم حفظ التعديلات' : '✓ Changes saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الحفظ' : 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const lookupCustomer = async () => {
    const email = createForm.customer_email.trim();
    if (!email) return;
    setLookupState('loading');
    try {
      const res = await axios.get(`${API_URL}/api/admin/customer-ads/customer-lookup?email=${encodeURIComponent(email)}`, { headers: getAuthHeaders() });
      setLookupState(res.data.customer);
    } catch {
      setLookupState('notfound');
    }
  };

  const createAd = async () => {
    setCreating(true);
    try {
      const payload = {
        customer_email: createForm.customer_email.trim(),
        category,
        title: createForm.title,
        description: createForm.description,
        currency: createForm.currency,
        status: createForm.status,
        phone_number: createForm.phone_number,
        make: createForm.make,
        model: createForm.model,
        plate_emirate: createForm.plate_emirate,
        plate_code: createForm.plate_code,
        plate_number: createForm.plate_number,
      };
      if (createForm.price !== '') payload.price = Number(createForm.price);
      if (createForm.year !== '') payload.year = Number(createForm.year);
      const res = await axios.post(`${API_URL}/api/admin/customer-ads`, payload, { headers: getAuthHeaders() });
      setAds((list) => [res.data.ad, ...list]);
      setShowCreate(false);
      setCreateForm(EMPTY_CREATE);
      setLookupState(null);
      toast.success(isAr ? '✓ تم إدراج الإعلان للعميل' : '✓ Ad created for customer');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل إدراج الإعلان' : 'Create failed'));
    } finally {
      setCreating(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-white text-sm focus:border-blue-500/50 focus:outline-none';
  const labelCls = 'block text-xs text-white/60 mb-1.5';

  return (
    <div data-testid={`customer-ads-${category}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Icon className={`h-6 w-6 ${meta.color}`} />
          <div>
            <h2 className="text-xl font-bold text-white">
              {isAr ? meta.titleAr : meta.titleEn}
              <span className="ms-2 text-sm font-normal text-[#808080]">({ads.length})</span>
            </h2>
            <p className="text-xs text-[#808080] mt-0.5">
              {isAr ? 'إعلانات العملاء — الحسابات الشخصية' : 'Customer ads — personal accounts'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => { setShowCreate(true); setCreateForm(EMPTY_CREATE); setLookupState(null); }}
            className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium flex items-center gap-2"
            data-testid={`customer-ads-add-btn-${category}`}
          >
            <Plus className="h-4 w-4" />
            {isAr ? 'إدراج إعلان لعميل' : 'Add Ad for Customer'}
          </button>
          <button
            onClick={fetchAds}
            disabled={loading}
            className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm hover:bg-white/10 flex items-center gap-2"
            data-testid={`customer-ads-refresh-${category}`}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {onBack && (
            <Button
              onClick={onBack}
              variant="outline"
              className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e] rounded-xl"
              data-testid={`customer-ads-back-${category}`}
            >
              {isAr ? 'رجوع' : 'Back'}
            </Button>
          )}
        </div>
      </div>

      {/* Search + status filter */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 h-4 w-4 text-white/30" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isAr ? 'بحث (العنوان، الرقم، اسم العميل، الإيميل...)' : 'Search (title, number, customer name, email...)'}
            className="w-full ps-9 pe-3 py-2 bg-[#0a0a14] border border-white/10 rounded-xl text-white text-sm focus:border-blue-500/50 focus:outline-none"
            data-testid={`customer-ads-search-${category}`}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 bg-[#0a0a14] border border-white/10 rounded-xl text-white text-sm focus:outline-none"
          data-testid={`customer-ads-status-filter-${category}`}
        >
          <option value="">{isAr ? 'كل الحالات' : 'All statuses'}</option>
          {Object.entries(STATUS_META).map(([k, v]) => (
            <option key={k} value={k}>{isAr ? v.ar : v.en}</option>
          ))}
        </select>
      </div>

      {/* Cards */}
      {loading && ads.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-52 bg-white/[0.02] rounded-2xl animate-pulse" />)}
        </div>
      ) : ads.length === 0 ? (
        <div className="text-center py-16 bg-white/[0.02] rounded-2xl border border-white/10">
          <Icon className={`h-16 w-16 mx-auto mb-4 opacity-30 ${meta.color}`} />
          <p className="text-lg text-white">{isAr ? 'لا توجد إعلانات حالياً' : 'No ads yet'}</p>
          <p className="text-sm mt-2 text-[#606060]">
            {isAr ? 'ستظهر هنا إعلانات العملاء فور وصولها من منصة العملاء' : 'Customer ads will appear here once submitted'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {ads.map((ad) => {
            const st = STATUS_META[ad.status] || STATUS_META.pending;
            const plate = ad.plate || {};
            return (
              <div
                key={ad.id}
                className={`p-4 bg-white/[0.02] rounded-2xl border border-white/10 ${meta.border} transition-all flex flex-col`}
                data-testid={`customer-ad-card-${ad.id}`}
              >
                {/* Top row */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className={`w-10 h-10 rounded-xl ${meta.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`h-5 w-5 ${meta.color}`} />
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${st.cls}`} data-testid={`customer-ad-status-${ad.id}`}>
                    {isAr ? st.ar : st.en}
                  </span>
                </div>

                {/* Headline */}
                {ad.category === 'car_plate' && plate.emirate && hasPlateDesign((plate.emirate || '').toLowerCase().replace(/ /g, '_'), 'private') ? (
                  <div className="mb-2 flex justify-center">
                    <UAEPlate
                      emirate={(plate.emirate || '').toLowerCase().replace(/ /g, '_')}
                      code={plate.code || ''}
                      number={plate.number || ''}
                      width={220}
                    />
                  </div>
                ) : null}
                <p className="font-bold text-white text-lg mb-1 truncate" dir={ad.category === 'phone_number' ? 'ltr' : undefined} style={ad.category === 'phone_number' ? { textAlign: isAr ? 'right' : 'left' } : undefined}>
                  {adHeadline(ad, isAr)}
                </p>
                {ad.title && ad.category !== 'phone_number' && ad.title !== adHeadline(ad, isAr) && (
                  <p className="text-xs text-white/50 truncate mb-1">{ad.title}</p>
                )}
                {ad.price != null && ad.price !== '' && (
                  <p className="text-[#c9a227] font-bold text-sm mb-1">
                    {Number(ad.price).toLocaleString()} {ad.currency || 'AED'}
                  </p>
                )}
                {ad.description && <p className="text-xs text-white/50 line-clamp-2 mb-2">{ad.description}</p>}

                {/* Customer info */}
                <div className="mt-auto p-2.5 rounded-xl bg-black/30 border border-white/5 mb-3">
                  <div className="flex items-center gap-1.5 text-xs text-white/80">
                    <User className="h-3 w-3 text-white/40" />
                    <span className="truncate font-medium">{ad.customer?.name || (isAr ? 'عميل' : 'Customer')}</span>
                    {ad.source === 'admin' && (
                      <span className="ms-auto text-[9px] px-1.5 py-0.5 rounded bg-purple-500/15 border border-purple-500/40 text-purple-300">
                        {isAr ? 'أُدرج بواسطة الأدمن' : 'Added by admin'}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-white/40 truncate mt-0.5" dir="ltr">
                    {ad.customer?.email}{ad.customer?.phone ? ` · ${ad.customer.phone}` : ''}
                  </p>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button
                    onClick={() => openEdit(ad)}
                    className="px-2.5 py-1.5 rounded-lg bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 text-xs flex items-center gap-1"
                    data-testid={`customer-ad-edit-${ad.id}`}
                  >
                    <Pencil className="h-3 w-3" />{isAr ? 'تعديل' : 'Edit'}
                  </button>
                  {ad.status === 'paused' || ad.status === 'rejected' || ad.status === 'pending' ? (
                    <button
                      onClick={() => setStatus(ad, 'active')}
                      disabled={busyId === ad.id}
                      className="px-2.5 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 text-xs flex items-center gap-1 disabled:opacity-40"
                      data-testid={`customer-ad-activate-${ad.id}`}
                    >
                      <Play className="h-3 w-3" />{isAr ? 'تفعيل' : 'Activate'}
                    </button>
                  ) : (
                    <button
                      onClick={() => setStatus(ad, 'paused')}
                      disabled={busyId === ad.id}
                      className="px-2.5 py-1.5 rounded-lg bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 text-xs flex items-center gap-1 disabled:opacity-40"
                      data-testid={`customer-ad-pause-${ad.id}`}
                    >
                      <Pause className="h-3 w-3" />{isAr ? 'إيقاف مؤقت' : 'Pause'}
                    </button>
                  )}
                  {ad.status !== 'rejected' && (
                    <button
                      onClick={() => setStatus(ad, 'rejected')}
                      disabled={busyId === ad.id}
                      className="px-2.5 py-1.5 rounded-lg bg-orange-500/10 text-orange-300 hover:bg-orange-500/20 text-xs flex items-center gap-1 disabled:opacity-40"
                      data-testid={`customer-ad-reject-${ad.id}`}
                    >
                      <Ban className="h-3 w-3" />{isAr ? 'رفض' : 'Reject'}
                    </button>
                  )}
                  <button
                    onClick={() => setConfirmDelete(ad)}
                    disabled={busyId === ad.id}
                    className="px-2.5 py-1.5 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20 text-xs flex items-center gap-1 ms-auto disabled:opacity-40"
                    data-testid={`customer-ad-delete-${ad.id}`}
                  >
                    <Trash2 className="h-3 w-3" />{isAr ? 'حذف' : 'Delete'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit modal */}
      {createPortal(
        <AnimatePresence>
          {editing && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
              onClick={() => !saving && setEditing(null)}
            >
              <motion.div
                initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0a0a14] border border-blue-500/40 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
                data-testid="customer-ad-edit-modal"
              >
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Pencil className="h-5 w-5 text-blue-400" />
                    {isAr ? 'تعديل الإعلان' : 'Edit Ad'}
                  </h3>
                  <button onClick={() => setEditing(null)} disabled={saving} className="text-white/60 hover:text-white p-1"><X className="h-5 w-5" /></button>
                </div>

                {category === 'phone_number' && (
                  <div className="mb-4">
                    <label className={labelCls}>{isAr ? 'رقم الهاتف' : 'Phone Number'}</label>
                    <input dir="ltr" value={editForm.phone_number} onChange={(e) => setEditForm((f) => ({ ...f, phone_number: e.target.value }))} className={inputCls} data-testid="edit-phone-number" />
                  </div>
                )}
                {category === 'car' && (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div>
                      <label className={labelCls}>{isAr ? 'الشركة' : 'Make'}</label>
                      <input value={editForm.make} onChange={(e) => setEditForm((f) => ({ ...f, make: e.target.value }))} className={inputCls} data-testid="edit-car-make" />
                    </div>
                    <div>
                      <label className={labelCls}>{isAr ? 'الموديل' : 'Model'}</label>
                      <input value={editForm.model} onChange={(e) => setEditForm((f) => ({ ...f, model: e.target.value }))} className={inputCls} data-testid="edit-car-model" />
                    </div>
                    <div>
                      <label className={labelCls}>{isAr ? 'السنة' : 'Year'}</label>
                      <input type="number" value={editForm.year} onChange={(e) => setEditForm((f) => ({ ...f, year: e.target.value }))} className={inputCls} data-testid="edit-car-year" />
                    </div>
                  </div>
                )}
                {category === 'car_plate' && (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div>
                      <label className={labelCls}>{isAr ? 'الإمارة' : 'Emirate'}</label>
                      <input value={editForm.plate_emirate} onChange={(e) => setEditForm((f) => ({ ...f, plate_emirate: e.target.value }))} className={inputCls} data-testid="edit-plate-emirate" />
                    </div>
                    <div>
                      <label className={labelCls}>{isAr ? 'الكود' : 'Code'}</label>
                      <input value={editForm.plate_code} onChange={(e) => setEditForm((f) => ({ ...f, plate_code: e.target.value }))} className={inputCls} data-testid="edit-plate-code" />
                    </div>
                    <div>
                      <label className={labelCls}>{isAr ? 'الرقم' : 'Number'}</label>
                      <input dir="ltr" value={editForm.plate_number} onChange={(e) => setEditForm((f) => ({ ...f, plate_number: e.target.value }))} className={inputCls} data-testid="edit-plate-number" />
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <label className={labelCls}>{isAr ? 'العنوان' : 'Title'}</label>
                  <input value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} className={inputCls} data-testid="edit-ad-title" />
                </div>
                <div className="mb-4">
                  <label className={labelCls}>{isAr ? 'السعر' : 'Price'}</label>
                  <input type="number" min="0" value={editForm.price} onChange={(e) => setEditForm((f) => ({ ...f, price: e.target.value }))} className={inputCls} data-testid="edit-ad-price" />
                </div>
                <div className="mb-5">
                  <label className={labelCls}>{isAr ? 'الوصف' : 'Description'}</label>
                  <textarea rows={3} value={editForm.description} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} className={`${inputCls} resize-none`} data-testid="edit-ad-description" />
                </div>

                <div className="flex gap-3 pt-4 border-t border-white/10">
                  <button onClick={() => setEditing(null)} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 disabled:opacity-50">
                    {isAr ? 'إلغاء' : 'Cancel'}
                  </button>
                  <button onClick={saveEdit} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2" data-testid="edit-ad-save">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {isAr ? 'حفظ التعديلات' : 'Save Changes'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body)}

      {/* Create-for-customer modal */}
      {createPortal(
        <AnimatePresence>
          {showCreate && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
              onClick={() => !creating && setShowCreate(false)}
            >
              <motion.div
                initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0a0a14] border border-blue-500/40 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
                data-testid="customer-ad-create-modal"
              >
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Plus className="h-5 w-5 text-blue-400" />
                    {isAr ? `إدراج إعلان — ${meta.titleAr}` : `Add Ad — ${meta.titleEn}`}
                  </h3>
                  <button onClick={() => setShowCreate(false)} disabled={creating} className="text-white/60 hover:text-white p-1"><X className="h-5 w-5" /></button>
                </div>

                {/* Customer email lookup */}
                <div className="mb-4">
                  <label className={labelCls}>{isAr ? 'إيميل العميل' : 'Customer Email'} <span className="text-red-400">*</span></label>
                  <div className="flex gap-2">
                    <input
                      dir="ltr"
                      value={createForm.customer_email}
                      onChange={(e) => { setCreateForm((f) => ({ ...f, customer_email: e.target.value })); setLookupState(null); }}
                      onKeyDown={(e) => e.key === 'Enter' && lookupCustomer()}
                      placeholder="customer@email.com"
                      className={inputCls}
                      data-testid="create-ad-customer-email"
                    />
                    <button
                      onClick={lookupCustomer}
                      disabled={lookupState === 'loading' || !createForm.customer_email.trim()}
                      className="px-3 py-2 rounded-lg bg-blue-500/15 border border-blue-500/40 text-blue-300 text-xs hover:bg-blue-500/25 flex items-center gap-1.5 disabled:opacity-40 whitespace-nowrap"
                      data-testid="create-ad-lookup-btn"
                    >
                      {lookupState === 'loading' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                      {isAr ? 'بحث' : 'Lookup'}
                    </button>
                  </div>
                  {lookupState && lookupState !== 'loading' && lookupState !== 'notfound' && (
                    <div className="mt-2 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2 text-xs text-emerald-300" data-testid="create-ad-customer-found">
                      <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                      <span className="font-bold">{lookupState.name}</span>
                      <span className="text-white/50" dir="ltr">{lookupState.email}{lookupState.phone ? ` · ${lookupState.phone}` : ''}</span>
                    </div>
                  )}
                  {lookupState === 'notfound' && (
                    <div className="mt-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-2 text-xs text-red-300" data-testid="create-ad-customer-notfound">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      {isAr ? 'لا يوجد عميل بهذا الإيميل في قاعدة البيانات' : 'No customer with this email in the database'}
                    </div>
                  )}
                </div>

                {category === 'phone_number' && (
                  <div className="mb-4">
                    <label className={labelCls}>{isAr ? 'رقم الهاتف المعروض' : 'Phone Number for Sale'} <span className="text-red-400">*</span></label>
                    <input dir="ltr" value={createForm.phone_number} onChange={(e) => setCreateForm((f) => ({ ...f, phone_number: e.target.value }))} placeholder="050 123 4567" className={inputCls} data-testid="create-ad-phone-number" />
                  </div>
                )}
                {category === 'car' && (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div>
                      <label className={labelCls}>{isAr ? 'الشركة' : 'Make'}</label>
                      <input value={createForm.make} onChange={(e) => setCreateForm((f) => ({ ...f, make: e.target.value }))} placeholder="Nissan" className={inputCls} data-testid="create-ad-car-make" />
                    </div>
                    <div>
                      <label className={labelCls}>{isAr ? 'الموديل' : 'Model'}</label>
                      <input value={createForm.model} onChange={(e) => setCreateForm((f) => ({ ...f, model: e.target.value }))} placeholder="Patrol" className={inputCls} data-testid="create-ad-car-model" />
                    </div>
                    <div>
                      <label className={labelCls}>{isAr ? 'السنة' : 'Year'}</label>
                      <input type="number" value={createForm.year} onChange={(e) => setCreateForm((f) => ({ ...f, year: e.target.value }))} placeholder="2024" className={inputCls} data-testid="create-ad-car-year" />
                    </div>
                  </div>
                )}
                {category === 'car_plate' && (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div>
                      <label className={labelCls}>{isAr ? 'الإمارة' : 'Emirate'}</label>
                      <input value={createForm.plate_emirate} onChange={(e) => setCreateForm((f) => ({ ...f, plate_emirate: e.target.value }))} placeholder={isAr ? 'Dubai' : 'Dubai'} className={inputCls} data-testid="create-ad-plate-emirate" />
                    </div>
                    <div>
                      <label className={labelCls}>{isAr ? 'الكود' : 'Code'}</label>
                      <input value={createForm.plate_code} onChange={(e) => setCreateForm((f) => ({ ...f, plate_code: e.target.value }))} placeholder="A" className={inputCls} data-testid="create-ad-plate-code" />
                    </div>
                    <div>
                      <label className={labelCls}>{isAr ? 'الرقم' : 'Number'}</label>
                      <input dir="ltr" value={createForm.plate_number} onChange={(e) => setCreateForm((f) => ({ ...f, plate_number: e.target.value }))} placeholder="12345" className={inputCls} data-testid="create-ad-plate-number" />
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <label className={labelCls}>{isAr ? 'العنوان (اختياري)' : 'Title (optional)'}</label>
                  <input value={createForm.title} onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))} className={inputCls} data-testid="create-ad-title" />
                </div>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div>
                    <label className={labelCls}>{isAr ? 'السعر' : 'Price'}</label>
                    <input type="number" min="0" value={createForm.price} onChange={(e) => setCreateForm((f) => ({ ...f, price: e.target.value }))} className={inputCls} data-testid="create-ad-price" />
                  </div>
                  <div>
                    <label className={labelCls}>{isAr ? 'الحالة' : 'Status'}</label>
                    <select value={createForm.status} onChange={(e) => setCreateForm((f) => ({ ...f, status: e.target.value }))} className={inputCls} data-testid="create-ad-status">
                      {Object.entries(STATUS_META).map(([k, v]) => (
                        <option key={k} value={k}>{isAr ? v.ar : v.en}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mb-5">
                  <label className={labelCls}>{isAr ? 'الوصف' : 'Description'}</label>
                  <textarea rows={2} value={createForm.description} onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))} className={`${inputCls} resize-none`} data-testid="create-ad-description" />
                </div>

                <div className="flex gap-3 pt-4 border-t border-white/10">
                  <button onClick={() => setShowCreate(false)} disabled={creating} className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 disabled:opacity-50">
                    {isAr ? 'إلغاء' : 'Cancel'}
                  </button>
                  <button
                    onClick={createAd}
                    disabled={creating || !createForm.customer_email.trim()}
                    className="flex-1 py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="create-ad-save"
                  >
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {isAr ? 'إدراج الإعلان' : 'Create Ad'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body)}

      {/* Delete confirmation */}
      {createPortal(
        <AnimatePresence>
          {confirmDelete && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
              onClick={() => setConfirmDelete(null)}
            >
              <motion.div
                initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-[#0a0a14] border border-red-500/40 rounded-2xl p-6 max-w-md w-full"
                data-testid="customer-ad-confirm-delete"
              >
                <div className="flex items-center gap-3 mb-4">
                  <AlertCircle className="h-6 w-6 text-red-400" />
                  <h3 className="text-lg font-bold text-white">{isAr ? 'تأكيد الحذف' : 'Confirm Delete'}</h3>
                </div>
                <p className="text-white/80 text-sm mb-2">
                  {isAr ? 'هل أنت متأكد من حذف هذا الإعلان نهائياً؟' : 'Permanently delete this ad?'}
                </p>
                <p className="text-white text-sm font-semibold mb-5">&quot;{adHeadline(confirmDelete, isAr)}&quot;</p>
                <div className="flex gap-3">
                  <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10">
                    {isAr ? 'إلغاء' : 'Cancel'}
                  </button>
                  <button
                    onClick={() => deleteAd(confirmDelete)}
                    disabled={busyId === confirmDelete.id}
                    className="flex-1 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium disabled:opacity-50"
                    data-testid="customer-ad-confirm-delete-btn"
                  >
                    {isAr ? 'حذف نهائي' : 'Delete'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body)}
    </div>
  );
}
